import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, RefreshCw, Truck } from 'lucide-react';
import { useState } from 'react';

import type { VendorPickup } from '@terra/api-client';
import { STATE_NAME_BY_CODE, gstStateCode } from '@terra/utils';

import { api } from '../lib/api';

const STATUS = {
  SYNCED: { label: 'Ready for pickup', cls: 'bg-emerald-50 text-emerald-800 ring-emerald-200' },
  PENDING_VERIFICATION: {
    label: 'Phone verification pending',
    cls: 'bg-amber-50 text-amber-800 ring-amber-200',
  },
  FAILED: { label: 'Not registered', cls: 'bg-rose-50 text-rose-800 ring-rose-200' },
} as const;

/**
 * The seller's pickup address as the courier will see it, and its pickup
 * location at Shiprocket — checked here against the KYC address proof before
 * approval, and registered automatically when the store is approved.
 */
export function SellerPickupPanel({
  vendorId,
  pickup,
}: {
  vendorId: string;
  pickup: VendorPickup | undefined;
}) {
  const qc = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const sync = useMutation({
    mutationFn: () => api.vendors.syncPickup(vendorId),
    onSuccess: (r) => {
      setMessage(
        r.shiprocket.status === 'SYNCED'
          ? 'Registered and verified at Shiprocket.'
          : (r.shiprocket.error ?? r.problems.join(' ')),
      );
      void qc.invalidateQueries({ queryKey: ['vendor-detail', vendorId] });
    },
    onError: (e: unknown) =>
      setMessage(e instanceof Error ? e.message : 'Could not reach Shiprocket.'),
  });

  const w = pickup?.warehouse ?? null;
  const status = pickup?.shiprocket.status ?? null;
  const state = w ? (STATE_NAME_BY_CODE[gstStateCode(w.stateCode) ?? ''] ?? w.stateCode) : '';

  return (
    <section className="space-y-2">
      <h4 className="text-ink-muted flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
        <MapPin className="h-4 w-4" /> Pickup address
      </h4>
      {w ? (
        <div className="border-border/60 rounded-xl border px-3 py-2.5 text-[12.5px] leading-relaxed">
          <p className="text-ink font-semibold">
            {[w.line1, w.line2, w.landmark].filter(Boolean).join(', ')}
          </p>
          <p className="text-ink-muted">
            {w.city}, {state} {w.pincode}
          </p>
          <p className="text-ink-muted">
            Contact: {w.contactName ?? '—'} · {w.contactPhone ?? '—'}
          </p>
        </div>
      ) : (
        <p className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-800">
          The seller has not given a pickup address.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[12px]">
          <Truck className="text-ink-subtle h-4 w-4" />
          <span className="text-ink-muted font-semibold">Shiprocket:</span>
          {status ? (
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ring-inset ${STATUS[status].cls}`}
            >
              {STATUS[status].label}
            </span>
          ) : (
            <span className="text-ink-muted">not registered yet (happens on approval)</span>
          )}
          {pickup?.shiprocket.code && (
            <span className="text-ink-subtle font-mono text-[11px]">{pickup.shiprocket.code}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            setMessage(null);
            sync.mutate();
          }}
          disabled={sync.isPending || !w}
          className="border-border hover:bg-surface-subtle inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[12px] font-bold disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${sync.isPending ? 'animate-spin' : ''}`} />
          {status ? 'Check again' : 'Register now'}
        </button>
      </div>
      {(message ?? pickup?.shiprocket.error) && (
        <p
          className={`text-[12px] font-medium ${status === 'SYNCED' ? 'text-emerald-700' : 'text-amber-800'}`}
        >
          {message ?? pickup?.shiprocket.error}
        </p>
      )}
    </section>
  );
}
